import React, { useEffect, useState } from "react";

import styles from "./FormStep2.module.scss";
import { getParties } from "../../features/newJobwork/newJobworkSlice";
import { useAppSelector } from "../../app/hooks";
import { PartyProps } from "../../types/jobwork";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { GiCancel } from "react-icons/gi";
import { OptionsProps } from "../../types/order";
import Select from "react-select";
import { selectStyles } from "../../static/styles";
import PartiesForm from "./PartiesForm";

const FormStep2 = () => {
  const parties = useAppSelector(getParties);

  const {
    getValues,
    control,
    register,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  const [selectedParty, setSelectedParty] = useState<PartyProps | null>(null);
  const [itemsOption, setItemsOption] = useState<OptionsProps[]>([]);
  const [type, setType] = useState<number>(0);

  const handleAppendNewItem = () => {
    append({
      item_id: 0,
      item_weight: null,
      tar_weight: null,
      order_parties: [{ party_id: 0 }],
    });
  };

  const handleChangeItem = (item_id: string, index: number) => {
    setValue(`items.${index}.item_id`, item_id);
    setValue(`items.${index}.order_parties`, [{ party_id: 0 }]);
    clearErrors(`items.${index}.item_id`);
  };

  useEffect(() => {
    const party_id = getValues("party_id");
    const type = getValues("type");

    setType(type);

    if (party_id) {
      const party = parties.find((p) => p.id === party_id);

      if (party) {
        setSelectedParty(party);

        setItemsOption(
          party.items.map((item) => ({
            value: item.item.id || 0,
            label: item.item.name || "",
          })) || []
        );
      }
    }
  }, [getValues, parties]);

  return (
    <div className={styles.FormStep2}>
      <h2 className="relative w-full text-2xl underline text-primary-1 font-bold mb-4">
        {selectedParty?.name}
      </h2>

      {fields.map((item, index) => (
        <div className="mt-6" key={index}>
          <h2 className="relative w-full text-2xl underline text-primary-1 font-bold mb-4">
            આઈટમ - {index + 1}
            {index > 0 && (
              <GiCancel
                onClick={() => remove(index)}
                className="absolute z-10 shadow bg-white top-1 right-1 text-3xl text-primary-1 border-2 border-primary-1 p-1 rounded-full"
              />
            )}
          </h2>

          <div className="relative px-2 py-4 w-full mt-6 before:content[''] before:bg-gradient-to-r before:from-primary-1 before:to-primary-2 before:absolute before:top-0 before:left-0 before:w-full before:h-full before:rounded before:-z-[1]">
            <div className="">
              <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded">
                <Controller
                  {...register(`items.${index}.item_id`, {
                    required: true,
                    min: 1,
                  })}
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={
                        itemsOption.find((val) => val.value === field?.value) ||
                        null
                      }
                      className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      styles={selectStyles}
                      placeholder={"આઈટમ નું નામ સિલેક્ટ કરો"}
                      options={itemsOption}
                      onChange={(val: any) =>
                        handleChangeItem(val?.value, index)
                      }
                    />
                  )}
                />
              </div>
              {Array.isArray(errors?.items) && errors.items[index]?.item_id && (
                <span className="text-red-500">આઈટમ નું નામ સિલેક્ટ કરો</span>
              )}
            </div>

            <div className="mt-6">
              <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded">
                <input
                  className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                  type="number"
                  step="any"
                  pattern="[0-9]+(\.[0-9]+)?"
                  placeholder="આઈટમ નો વજન દાખલ કરો"
                  {...register(`items.${index}.item_weight`, {
                    required: true,
                  })}
                  onFocus={(e) =>
                    e.target.addEventListener(
                      "wheel",
                      function (e) {
                        e.preventDefault();
                      },
                      { passive: false }
                    )
                  }
                />
              </div>
              {Array.isArray(errors?.items) &&
                errors.items[index]?.item_weight && (
                  <span className="text-red-500">આઈટમ નો વજન દાખલ કરો</span>
                )}
            </div>

            <div className={`mt-6 ${type === 1 && "hidden"}`}>
              <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded">
                <input
                  className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                  type="number"
                  step="any"
                  pattern="[0-9]+(\.[0-9]+)?"
                  placeholder="તાર નો વજન દાખલ કરો"
                  {...register(`items.${index}.tar_weight`, {
                    required: type === 1 ? false : true,
                  })}
                  onFocus={(e) =>
                    e.target.addEventListener(
                      "wheel",
                      function (e) {
                        e.preventDefault();
                      },
                      { passive: false }
                    )
                  }
                />
              </div>
              {Array.isArray(errors?.items) &&
                errors.items[index]?.tar_weight && (
                  <span className="text-red-500">તાર નો વજન દાખલ કરો</span>
                )}
            </div>

            <PartiesForm index={index} />
          </div>
        </div>
      ))}

      <button
        className="mx-auto mt-8 shadow rounded-3xl text-3xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
        type="button"
        onClick={handleAppendNewItem}
      >
        આઈટમ ઉમેરો
      </button>
    </div>
  );
};
export default FormStep2;
